import { layoutMapLabel } from "./mapLabelLayout";
import { assertNever } from "./assertNever";
import {
  LANDMARK_BASE_PX,
  landmarkInk,
  landmarkScale,
  PATH_WEIGHT_PX,
  type MapDocument,
  type MapGrid,
  type MapLandmark,
  type MapLabel,
  labelFontPx,
  labelInk,
  MAP_LABEL_FONTS,
  type MapLabelFont,
  type MapLayer,
  type MapNode,
  type MapPath,
  type MapPathStyle,
} from "./mapDocument";
import { isJust, isNothing, just, nothing, toMaybe, type Maybe } from "./maybe";
import { landmarkIconById, type MapLandmarkIcon } from "./mapLandmarkIcons";

/** Saved map coordinates, independent of pan, zoom, or display density. */
export type WorldPoint = { x: number; y: number };
/** CSS pixels relative to the canvas top-left, not physical bitmap pixels. */
export type ScreenPoint = { x: number; y: number };

export function landmarkDrawSize(
  landmark: MapLandmark,
  zoom: number,
): { width: number; height: number } {
  const scale = landmarkScale(landmark) * zoom;
  const icon = landmarkIconById(landmark.iconId ?? "");
  if (isJust(icon) && isJust(icon.value.nativeSize)) {
    return {
      width: icon.value.nativeSize.value.width * scale * (landmark.scaleX ?? 1),
      height:
        icon.value.nativeSize.value.height * scale * (landmark.scaleY ?? 1),
    };
  }
  return {
    width: LANDMARK_BASE_PX * scale * (landmark.scaleX ?? 1),
    height: LANDMARK_BASE_PX * scale * (landmark.scaleY ?? 1),
  };
}

/** The viewport stores the world point at the canvas center. Zoom scales
 * distances from that center; it does not change the saved coordinates. */
export function worldToScreen(
  point: WorldPoint,
  viewport: MapDocument["viewport"],
  canvasWidth: number,
  canvasHeight: number,
): ScreenPoint {
  return {
    x: (point.x - viewport.x) * viewport.zoom + canvasWidth / 2,
    y: (point.y - viewport.y) * viewport.zoom + canvasHeight / 2,
  };
}

export function screenToWorld(
  point: ScreenPoint,
  viewport: MapDocument["viewport"],
  canvasWidth: number,
  canvasHeight: number,
): WorldPoint {
  return {
    x: (point.x - canvasWidth / 2) / viewport.zoom + viewport.x,
    y: (point.y - canvasHeight / 2) / viewport.zoom + viewport.y,
  };
}

export function snapWorldPoint(point: WorldPoint, grid: MapGrid): WorldPoint {
  if (grid.snap === false) return point;
  const size = grid.size;
  return {
    x: Math.round(point.x / size) * size,
    y: Math.round(point.y / size) * size,
  };
}

export function distance(a: WorldPoint, b: WorldPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

/** Join radius in world units — scales with zoom at call sites via threshold. */
export function joinThresholdWorld(zoom: number): number {
  return 12 / Math.max(zoom, 0.25);
}

export function findNodeNear(
  layer: MapLayer,
  point: WorldPoint,
  zoom: number,
): Maybe<MapNode> {
  const threshold = joinThresholdWorld(zoom);
  let best: Maybe<MapNode> = nothing;
  let bestDist = threshold;
  for (const node of layer.nodes) {
    const d = distance(node, point);
    if (d <= bestDist) {
      best = just(node);
      bestDist = d;
    }
  }
  return best;
}

export function nodeById(layer: MapLayer, id: string): Maybe<MapNode> {
  return toMaybe(layer.nodes.find((n) => n.id === id));
}

export function pathPoints(layer: MapLayer, path: MapPath): WorldPoint[] {
  const points: WorldPoint[] = [];
  for (const id of path.nodeIds) {
    const node = nodeById(layer, id);
    if (isJust(node)) points.push({ x: node.value.x, y: node.value.y });
  }
  return points;
}

/** Catmull-Rom tangent handles for the open segment from `p1` → `p2`. */
export function catmullRomCubicControls(
  p0: WorldPoint,
  p1: WorldPoint,
  p2: WorldPoint,
  p3: WorldPoint,
): { cp1: WorldPoint; cp2: WorldPoint } {
  return {
    cp1: {
      x: p1.x + (p2.x - p0.x) / 6,
      y: p1.y + (p2.y - p0.y) / 6,
    },
    cp2: {
      x: p2.x - (p3.x - p1.x) / 6,
      y: p2.y - (p3.y - p1.y) / 6,
    },
  };
}

function cubicBezierPoint(
  p0: WorldPoint,
  p1: WorldPoint,
  p2: WorldPoint,
  p3: WorldPoint,
  t: number,
): WorldPoint {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

/**
 * Sample an open polyline as a Catmull-Rom spline (same bends as path ink).
 * Two anchors stay a straight densified segment; three+ curve through anchors.
 */
export function sampleSmoothPolyline(
  points: ReadonlyArray<WorldPoint>,
  stepWorld: number,
): WorldPoint[] {
  if (points.length === 0) return [];
  const first = points[0];
  if (typeof first === "undefined") return [];
  if (points.length === 1) return [{ x: first.x, y: first.y }];
  const step = Math.max(0.5, stepWorld);
  if (points.length === 2) {
    const a = points[0]!;
    const b = points[1]!;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const count = Math.max(1, Math.ceil(dist / step));
    const out: WorldPoint[] = [{ x: a.x, y: a.y }];
    for (let s = 1; s <= count; s++) {
      const t = s / count;
      out.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
      });
    }
    return out;
  }
  const n = points.length;
  const out: WorldPoint[] = [{ x: first.x, y: first.y }];
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[Math.max(i - 1, 0)]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[Math.min(i + 2, n - 1)]!;
    const { cp1, cp2 } = catmullRomCubicControls(p0, p1, p2, p3);
    const chord = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    // Extra samples so curved spans stay dense enough for stamp overlap.
    const count = Math.max(1, Math.ceil((chord * 1.25) / step));
    for (let s = 1; s <= count; s++) {
      out.push(cubicBezierPoint(p1, cp1, cp2, p2, s / count));
    }
  }
  return out;
}

/** Catmull-Rom → cubic Bézier segments for smooth ink through anchors. */
export function traceSmoothPath(
  ctx: CanvasRenderingContext2D,
  points: WorldPoint[],
  toScreen: (p: WorldPoint) => ScreenPoint,
  closed = false,
): void {
  if (points.length < 2) return;
  const screen = points.map(toScreen);
  const n = screen.length;
  ctx.beginPath();
  ctx.moveTo(screen[0]!.x, screen[0]!.y);
  if (n === 2) {
    ctx.lineTo(screen[1]!.x, screen[1]!.y);
    if (closed) ctx.closePath();
    return;
  }
  const at = (i: number): ScreenPoint => screen[((i % n) + n) % n]!;
  const last = closed ? n : n - 1;
  for (let i = 0; i < last; i++) {
    const p0 = closed ? at(i - 1) : screen[Math.max(i - 1, 0)]!;
    const p1 = screen[i]!;
    const p2 = closed ? at(i + 1) : screen[i + 1]!;
    const p3 = closed ? at(i + 2) : screen[Math.min(i + 2, n - 1)]!;
    const { cp1, cp2 } = catmullRomCubicControls(p0, p1, p2, p3);
    ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, p2.x, p2.y);
  }
  if (closed) ctx.closePath();
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  doc: MapDocument,
  width: number,
  height: number,
  paperColor: string,
  lineColor: string,
): void {
  ctx.fillStyle = paperColor;
  ctx.fillRect(0, 0, width, height);
  if (doc.grid.visible === false) return;
  const size = doc.grid.size * doc.viewport.zoom;
  if (size < 6) return;
  const origin = worldToScreen({ x: 0, y: 0 }, doc.viewport, width, height);
  const offsetX = ((origin.x % size) + size) % size;
  const offsetY = ((origin.y % size) + size) % size;
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = offsetX; x <= width; x += size) {
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, height);
  }
  for (let y = offsetY; y <= height; y += size) {
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(width, y + 0.5);
  }
  ctx.stroke();
}

export function pathLineDash(style: MapPathStyle, width: number): number[] {
  switch (style) {
    case "solid":
      return [];
    case "dashed":
      return [width * 4, width * 3];
    case "dotted":
      return [0, width * 2.5];
    default:
      return assertNever(style);
  }
}

export function pathLineWidth(weight: MapPath["weight"], zoom: number): number {
  return Math.max(0.75, PATH_WEIGHT_PX[weight] * zoom);
}

const EMOJI_FONT =
  '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';

const emojiTileCache = new Map<string, HTMLCanvasElement>();

/**
 * Color emoji glyphs overflow the font em-box, so canvas fillText clips their
 * corners. Stamp from a padded offscreen tile, centered on (x, y).
 */
export function drawCenteredEmoji(
  ctx: CanvasRenderingContext2D,
  emoji: string,
  x: number,
  y: number,
  size: number,
): void {
  if (emoji === null || emoji.length === 0) return;
  const dpr =
    typeof window !== "undefined"
      ? Math.max(1, window.devicePixelRatio ?? 1)
      : 1;
  const cssSize = Math.max(8, size);
  const px = Math.max(8, Math.round(cssSize * dpr));
  const key = `${px}:${emoji}`;
  let tile = emojiTileCache.get(key);
  if (typeof tile === "undefined") {
    const pad = Math.ceil(px * 0.45);
    const dim = px + pad * 2;
    tile = document.createElement("canvas");
    tile.width = dim;
    tile.height = dim;
    const off = tile.getContext("2d");
    if (off === null) return;
    off.font = `${px}px ${EMOJI_FONT}`;
    off.textAlign = "center";
    off.textBaseline = "middle";
    off.fillText(emoji, dim / 2, dim / 2);
    emojiTileCache.set(key, tile);
  }
  const drawn = tile;
  const cssDim = drawn.width / dpr;
  ctx.drawImage(drawn, x - cssDim / 2, y - cssDim / 2, cssDim, cssDim);
}

const svgImageCache = new Map<string, HTMLImageElement | "loading">();

function parseViewBox(icon: MapLandmarkIcon): {
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  const raw = icon.viewBox ?? "0 0 24 24";
  const parts = raw.split(/[\s,]+/).map(Number);
  const num = (index: number, fallback: number): number => {
    const n = parts[index];
    return typeof n === "number" && Number.isFinite(n) ? n : fallback;
  };
  return {
    minX: num(0, 0),
    minY: num(1, 0),
    width: num(2, 24),
    height: num(3, 24),
  };
}

function loadSvgImage(
  svg: string,
  color: string,
  onReady: () => void,
): Maybe<HTMLImageElement> {
  const key = `${color}\n${svg}`;
  const existing = svgImageCache.get(key);
  if (existing instanceof HTMLImageElement) return just(existing);
  if (existing === "loading") return nothing;
  svgImageCache.set(key, "loading");
  const tinted = svg.replaceAll("currentColor", color);
  const img = new Image();
  img.onload = () => {
    svgImageCache.set(key, img);
    onReady();
  };
  img.onerror = () => {
    svgImageCache.delete(key);
  };
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tinted)}`;
  return nothing;
}

export function drawLandmarkMark(
  ctx: CanvasRenderingContext2D,
  landmark: MapLandmark,
  x: number,
  y: number,
  zoom: number,
  onImageReady?: () => void,
): void {
  const size = landmarkDrawSize(landmark, zoom);
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(((landmark.rotation ?? 0) * Math.PI) / 180);
  ctx.scale(landmark.flipX === true ? -1 : 1, landmark.flipY === true ? -1 : 1);
  if (typeof landmark.iconId === "string" && landmark.iconId.length > 0) {
    const icon = landmarkIconById(landmark.iconId);
    if (isJust(icon)) {
      drawLandmarkIcon(
        ctx,
        icon.value,
        0,
        0,
        size,
        landmarkInk(landmark),
        onImageReady,
      );
      ctx.restore();
      return;
    }
  }
  if (typeof landmark.emoji === "string" && landmark.emoji.length > 0) {
    ctx.scale(1, size.height / size.width);
    drawCenteredEmoji(ctx, landmark.emoji, 0, 0, size.width);
  }
  ctx.restore();
}

export function drawLandmarkIcon(
  ctx: CanvasRenderingContext2D,
  icon: MapLandmarkIcon,
  x: number,
  y: number,
  size: { width: number; height: number },
  color: string,
  onImageReady?: () => void,
): void {
  if (typeof icon.svg === "string" && icon.svg.length > 0) {
    const img = loadSvgImage(icon.svg, color, () => onImageReady?.());
    if (isNothing(img)) return;
    const { width, height } = size;
    ctx.drawImage(img.value, x - width / 2, y - height / 2, width, height);
    return;
  }
  const paths = icon.paths;
  if (typeof paths === "undefined" || paths.length === 0) return;
  const box = parseViewBox(icon);
  ctx.save();
  ctx.translate(x - size.width / 2, y - size.height / 2);
  ctx.scale(size.width / box.width, size.height / box.height);
  ctx.translate(-box.minX, -box.minY);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.75;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const d of paths) {
    ctx.stroke(new Path2D(d));
  }
  ctx.restore();
}

export function hitTestPath(
  layer: MapLayer,
  path: MapPath,
  point: WorldPoint,
  zoom: number,
): boolean {
  // Picking approximates the smooth ink with straight anchor-to-anchor segments.
  const points = pathPoints(layer, path);
  if (points.length < 2) return false;
  const threshold = 8 / Math.max(zoom, 0.25);
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (distanceToSegment(point, a, b) <= threshold) return true;
  }
  return false;
}

/** Distance to the nearest point on a straight segment (including its ends). */
export function distanceToSegment(
  p: WorldPoint,
  a: WorldPoint,
  b: WorldPoint,
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0 && dy === 0) return distance(p, a);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)),
  );
  return distance(p, { x: a.x + t * dx, y: a.y + t * dy });
}

export function hitTestNode(
  node: MapNode,
  point: WorldPoint,
  zoom: number,
): boolean {
  return distance(node, point) <= 10 / Math.max(zoom, 0.25);
}

export function labelCanvasFont(
  fontPx: number,
  font: MapLabelFont = "cartographic",
): string {
  return `${fontPx}px ${MAP_LABEL_FONTS[font]}`;
}

export function measureMapLabel(
  ctx: CanvasRenderingContext2D,
  label: MapLabel,
  zoom: number,
): ReturnType<typeof layoutMapLabel> {
  const fontPx = labelFontPx(label, zoom);
  ctx.font = labelCanvasFont(fontPx, label.font);
  const curve = label.curve ?? 0;
  const spacing = (label.letterSpacing ?? 0) * zoom;
  const segmenter = new Intl.Segmenter([], { granularity: "grapheme" });
  const lines = label.text.split("\n").map((line) => {
    if (curve === 0 && spacing === 0)
      return [{ text: line, width: ctx.measureText(line).width }];
    let prefix = "";
    let previous = 0;
    return Array.from(segmenter.segment(line), ({ segment }) => {
      prefix += segment;
      const width = ctx.measureText(prefix).width;
      const glyph = { text: segment, width: width - previous };
      previous = width;
      return glyph;
    });
  });
  return layoutMapLabel(lines, fontPx, curve, spacing, label.rotation ?? 0);
}

export function drawMapLabel(
  ctx: CanvasRenderingContext2D,
  label: MapLabel,
  point: ScreenPoint,
  zoom: number,
): void {
  ctx.save();
  const layout = measureMapLabel(ctx, label, zoom);
  ctx.fillStyle = labelInk(label);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  for (const glyph of layout.glyphs) {
    ctx.save();
    ctx.translate(point.x + glyph.x, point.y + glyph.y);
    ctx.rotate(glyph.angle);
    ctx.fillText(glyph.text, 0, 0);
    ctx.restore();
  }
  ctx.restore();
}
